<template>
  <div v-if="!isLoading">
    <Select
      v-model="selectedContact"
      :options="contacts"
      optionLabel="name"
      :optionValue="optionValueIsId ? 'id' : undefined"
      :loading="isSearching"
      showClear
      placeholder="Select an existing contact"
      fluid
      :pt="{
        label: {
          class: props.size === 'small' ? 'text-sm' : '',
        },
      }"
      :disabled="isDisabled"
      @hide="searchQuery = '', handleFilter()"
    >
      <template #header>
        <div class="p-4">
          <IconField>
            <InputText
              v-model="searchQuery"
              fluid
              @input="handleFilter"
            />
            <InputIcon class="pi pi-search" />
          </IconField>
        </div>
      </template>

      <template #option="slotProps">
        <div class="flex flex-col gap-1 py-1">
          <div class="font-medium">
            {{ slotProps.option.name }}
          </div>
          <div class="text-sm text-gray-600">
            <div
              v-if="slotProps.option.phone && slotProps.option.phone !== '-'"
              class="flex items-center gap-2"
            >
              <Icon
                name="lucide:phone"
                class="w-4 h-4"
              />
              {{ slotProps.option.phone }}
            </div>
            <div
              v-if="!slotProps.option.email.startsWith('cimpro.app+')"
              class="flex items-center gap-2"
            >
              <Icon
                name="lucide:mail"
                class="w-4 h-4"
              />
              {{ slotProps.option.email }}
            </div>
          </div>
        </div>
      </template>

      <template #empty>
        <div
          v-if="showCreateContact"
          class="mb-2 space-y-2"
        >
          <p>Couldn't find an existing contact?</p>
          <Button
            outlined
            label="Create Contact"
            @click="createContactDialog.toggleOpen()"
          />
        </div>
        <div
          v-else
          class="mb-2"
        >
          <p>No contact that matches your search.</p>
        </div>
      </template>
    </Select>

    <ContactCreate
      ref="createContactDialog"
      class="opacity-0 pointer-events-none absolute"
      @fetchContacts="
        (contact) => {
          useCreatedContact = contact
        }
      "
    />
  </div>

  <div v-else>
    <Skeleton
      height="2.5rem"
    />
  </div>
</template>

<script lang="ts" setup>
import type { Contact } from '@prisma/client'

const props = defineProps({
  size: {
    type: String,
    required: false,
  },
  showCreateContact: {
    type: Boolean,
    required: false,
    default: true,
  },
  optionValueIsId: {
    type: Boolean,
    required: false,
    default: true,
  },
  isDisabled: {
    type: Boolean,
    required: false,
    default: false,
  },
})

const formatContact = (contact: Contact) => ({
  id: contact.id,
  firstName: contact.firstName,
  lastName: contact.lastName,
  name: contact.firstName + ' ' + contact.lastName,
  phone: contact.phone,
  email: contact.email,
})

type IContact = ReturnType<typeof formatContact>

const selectedContact = defineModel<string | IContact>('selectedContact')
const useCreatedContact = defineModel<Contact>('useCreatedContact')

const createContactDialog = ref()

// Contacts state
const contacts = ref<IContact[]>([])
const { fetchContacts, fetchSelectedContacts } = useContacts()

// Loading states
const isLoading = ref(false)
const isSearching = ref(false)

// Debounce timer - When user types in the filter input, this will help to limit the number of API calls
// Check handleFilter function
let searchTimeout: NodeJS.Timeout | null = null

// Fetch contacts
const getAllContacts = async (searchQuery: string = '') => {
  isSearching.value = true

  let newContacts: IContact[] = []

  // fetch selected contact
  if (selectedContact.value) {
    const contactIdToFetch = props.optionValueIsId
      ? [selectedContact.value as string]
      : [(selectedContact.value as IContact).id]

    const resSelected = await fetchSelectedContacts(contactIdToFetch)
    if (resSelected.data) {
      newContacts = (resSelected.data as any[]).map(formatContact)
    }
  }

  // Fetch search results
  const res = await fetchContacts(1, 50, searchQuery)

  if (res.data) {
    const fetchedContacts = (res.data as any[]).map(formatContact)

    const contactsToAdd = fetchedContacts.filter(contact => !newContacts.find(existing => existing.id === contact.id))

    newContacts = [...newContacts, ...contactsToAdd]
  }

  contacts.value = newContacts
  isSearching.value = false
}

const searchQuery = ref<string>('')

// Handle filter event with debounce
const handleFilter = () => {
  // Clear previous timeout
  if (searchTimeout) {
    clearTimeout(searchTimeout)
  }

  // Debounce search for 300ms
  searchTimeout = setTimeout(() => {
    getAllContacts(searchQuery.value)
  }, 300)
}

watch(useCreatedContact, async (newValue) => {
  if (!newValue) {
    return
  }

  if (props.optionValueIsId) {
    selectedContact.value = newValue.id
  }
  else {
    selectedContact.value = formatContact(newValue)
  }

  await getAllContacts()
})

onMounted(async () => {
  isLoading.value = true
  await getAllContacts()
  isLoading.value = false
})

onBeforeUnmount(() => {
  if (searchTimeout) {
    clearTimeout(searchTimeout)
    searchTimeout = null
  }
})
</script>
